import { useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead, type NotificationItem } from '@/api/notifications';
import { CategoryIcon, timeAgo } from './notificationHelpers';

export default function NotificationBell() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data: unread } = useUnreadCount();
  const { data: inbox } = useNotifications(8);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  const open = Boolean(anchor);
  const items = inbox?.items ?? [];

  const onItem = (n: NotificationItem) => {
    if (!n.isRead) markRead.mutate(n.id);
    setAnchor(null);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Notifications">
          <Badge badgeContent={unread ?? 0} color="error" max={99}>
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '92vw' } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
          {(unread ?? 0) > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={() => markAll.mutate()}>Mark all read</Button>
          )}
        </Stack>
        <Divider />

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>You're all caught up. 🎉</Typography>
          )}
          {items.map((n) => (
            <ListItemButton key={n.id} onClick={() => onItem(n)} sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.25, bgcolor: n.isRead ? 'transparent' : 'action.hover' }}>
              <Box sx={{ mt: 0.25 }}><CategoryIcon category={n.category} level={n.level} /></Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: n.isRead ? 500 : 700 }}>{n.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{n.message}</Typography>
                <Typography variant="caption" color="text.disabled">{timeAgo(n.createdDate)}</Typography>
              </Box>
              {!n.isRead && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', mt: 0.75, flexShrink: 0 }} />}
            </ListItemButton>
          ))}
        </Box>

        <Divider />
        <Button fullWidth onClick={() => { setAnchor(null); navigate('/notifications'); }} sx={{ py: 1 }}>View all notifications</Button>
      </Popover>
    </>
  );
}
